import { createId } from '@paralleldrive/cuid2';
import { Button } from 'components/Button';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import { TextField } from 'components/Field/TextField';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { PageHeader } from 'components/PageHeader';
import { useAlert } from 'hooks/useAlert';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { smartText } from 'lib/str';
import { cloneDeep, isEqual } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  NAVIGATE_MODAL_BACK,
  doAseSetupFlagDelete,
  doAseSetupFlagSave,
} from 'store/actions';
import { AseSetupFlag } from 'types/Assessments';

export const FlagSetupModal: React.FC<
  ModalScreenProps<ModalScreens.AseSetupFlag>
> = ({ route, navigation }) => {
  const dispatch = useDispatch();
  const forceUpdate = useForceUpdate();
  const confirmUnsaved = useConfirmUnsaved();
  const alert = useAlert();

  const flagIsNew = !route.params.flag;

  const initValues = React.useRef<AseSetupFlag>(
    route.params.flag
      ? {
          id: route.params.flag.id,
          name: route.params.flag.name,
          description: route.params.flag.description,
          setInProgress: route.params.flag.setInProgress,
          setSubmitted: route.params.flag.setSubmitted,
          setApproved: route.params.flag.setApproved,
          showOnReport: route.params.flag.showOnReport,
        }
      : {
          id: createId(),
          name: '',
          description: '',
          setInProgress: false,
          setSubmitted: false,
          setApproved: false,
          showOnReport: false,
        },
  );
  const changes = React.useRef(cloneDeep(initValues.current));

  const checkIsDirty = React.useCallback(
    () => !isEqual(initValues.current, changes.current),
    [],
  );
  const isDirty = React.useRef<boolean>(false);
  isDirty.current = checkIsDirty();

  const handleChangeWithName = React.useCallback(
    <
      N extends keyof typeof changes.current,
      V extends (typeof changes.current)[N],
    >(
      name: N,
      value: V,
    ) => {
      changes.current[name] = value;
      if (isDirty.current !== checkIsDirty()) {
        forceUpdate();
      }
    },
    [checkIsDirty, forceUpdate],
  );

  const handleChangeWithNameForceUpdate = React.useCallback(
    <
      N extends keyof typeof changes.current,
      V extends (typeof changes.current)[N],
    >(
      name: N,
      value: V,
    ) => {
      changes.current[name] = value;
      forceUpdate();
    },
    [forceUpdate],
  );

  const handleSave = React.useCallback(() => {
    if (isDirty.current) {
      if (!changes.current.name.trim()) {
        alert({
          text: 'Name is required',
        });
        return;
      }
      dispatch(
        doAseSetupFlagSave({
          flag: changes.current,
          isNew: flagIsNew,
        }),
      );
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [alert, dispatch, flagIsNew]);

  const handleClose = () => {
    if (isDirty.current) {
      confirmUnsaved({
        onSave: () => handleSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  };

  const isDirtyVar = isDirty.current;
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(handleClose);

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title={flagIsNew ? 'New Flag' : 'Flag'}
          onPressCancelButton={handleClose}
          onPressSaveButton={isDirty.current ? handleSave : null}
        />
      }
    >
      <Card label="Name">
        <TextField
          name="name"
          onChangeWithName={handleChangeWithName}
          value={changes.current.name}
          onEsc={handleClose}
          autoFocusWeb
          autoFocusDelay
        />
      </Card>
      <Card label="Description">
        <TextAreaField
          name="description"
          onChangeWithName={handleChangeWithName}
          value={changes.current.description}
          onEsc={handleClose}
        />
      </Card>

      <Card
        label="Available In Assessment Statuses"
        explanation={smartText(
          'Choose when this flag can be set. ' +
            'Note: "In Progress" flags are not currently available when performing assessments in the app.',
        )}
      >
        <CardRow
          label="In Progress"
          checkedValue={changes.current.setInProgress}
          onPress={() =>
            handleChangeWithNameForceUpdate(
              'setInProgress',
              !changes.current.setInProgress,
            )
          }
        />
        <CardRow
          label="Submitted"
          checkedValue={changes.current.setSubmitted}
          onPress={() =>
            handleChangeWithNameForceUpdate(
              'setSubmitted',
              !changes.current.setSubmitted,
            )
          }
        />
        <CardRow
          label="Approved"
          checkedValue={changes.current.setApproved}
          onPress={() =>
            handleChangeWithNameForceUpdate(
              'setApproved',
              !changes.current.setApproved,
            )
          }
        />
      </Card>

      <Card
        label="In Report"
        explanation="Show this flag and who set it in PDF reports"
      >
        <CardRow
          label="Appears in Reports"
          checkedValue={changes.current.showOnReport}
          onPress={() =>
            handleChangeWithNameForceUpdate(
              'showOnReport',
              !changes.current.showOnReport,
            )
          }
        />
      </Card>

      {!flagIsNew && (
        <Button
          text="Delete Flag"
          destructive
          style={{ alignSelf: 'center' }}
          onPress={() =>
            dispatch(
              doAseSetupFlagDelete({
                flagId: changes.current.id,
                name: initValues.current.name,
              }),
            )
          }
        ></Button>
      )}
    </Page>
  );
};
